import { useState } from 'react';
import type { Party } from '@coreledger/shared-types';

// A compact, real-party-id-bound picker for BankTransactionsPage.tsx's
// "Suggested Ledger" column — deliberately not PartySelector.tsx (a much
// heavier invoice-specific form block with GSTIN/address/consignee fields).
// This needs only "search parties by name, click one, save" — selecting
// fires immediately (no blur-wait, unlike the free-text Expense-category
// cell next to it — a click is already an unambiguous discrete action),
// which is what lets a bank entry actually resolve to a real Party.id
// instead of just a fuzzy name string (see BankStatementEntry.partyId's
// own schema comment / party-ledger.service.ts's new 'bank' entry type).
interface Props {
  currentLabel?: string;
  saved: boolean; // mirrors categoryConfirmed — drives the same green-border "saved" cue the Expense cell uses
  parties: Party[];
  onSelect: (party: Party) => void;
}

export function PartyLedgerPicker({ currentLabel, saved, parties, onSelect }: Props) {
  const [search, setSearch] = useState(currentLabel ?? '');
  const [open, setOpen] = useState(false);

  const filtered = search.trim()
    ? parties.filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : parties.slice(0, 8);

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search parties…"
        style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', width: '100%', boxSizing: 'border-box', borderColor: saved ? 'var(--success)' : undefined }}
        title={saved ? 'Saved — linked to this party\'s Ledger' : currentLabel ? 'Suggested — not yet saved' : 'No suggestion'}
      />
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
          {filtered.map(p => (
            <div
              key={p.id}
              onMouseDown={() => { onSelect(p); setSearch(p.name); setOpen(false); }}
              style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}
            >
              {p.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
